"use client";

import { useState, useEffect } from "react";
import { Eye, EyeOff, Loader } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import "./SignUp.css";
import logo from "../../assets/DHMS_logo.webp";
import signup from "../../assets/signup1.webp";
import AuthService from "../../Services/authService";
import GoogleAuthService from "../../Services/googleAuthService";
import Toast from "../../Components/Modals/Toast/Toast";
import ValidationModal from "../../Components/Modals/Validation/Validation";

const CheckCircleIcon = ({ met }) =>
  met ? (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="9" cy="9" r="9" fill="#2a66b0" />
      <path d="M5 9.5l2.5 2.5 5.5-5.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="9" cy="9" r="8" stroke="#d1d5db" strokeWidth="1.5" fill="none" />
      <path d="M5 9.5l2.5 2.5 5.5-5.5" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

const SignupForm = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });

  // Initialize Google Sign-In when component mounts
  useEffect(() => {
    GoogleAuthService.initialize().catch((error) => {
      console.error("Failed to initialize Google Sign-In:", error);
    });
  }, []);

  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    const newErrors = {};
    // Full name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = "Full name must be at least 2 characters";
    }

    // Email validation - use strict rules
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      // Strict email validation
      const emailRegex = /^[a-zA-Z0-9._+%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const emailStr = formData.email.trim().toLowerCase();
      const [localPart, domainPart] = emailStr.split("@") || [];

      // Multiple validation checks
      const validations = [
        {
          check: !emailRegex.test(emailStr),
          error: "Please enter a valid email address",
        },
        {
          check: localPart && localPart.length < 2,
          error: "Email local part is too short",
        },
        {
          check: domainPart && domainPart.length < 5,
          error: "Email domain is invalid",
        },
        {
          check: emailStr.includes("..") || emailStr.includes("@@"),
          error: "Email format is invalid",
        },
        {
          check: [
            "test.com",
            "fake.com",
            "example.com",
            "localhost",
            "tempmail.com",
            "temp-mail.com",
            "throwaway.email",
            "guerrillamail.com",
            "10minutemail.com",
            "mailinator.com",
            "maildrop.cc",
            "trashmail.com",
            "yopmail.com",
          ].some((fake) => domainPart?.includes(fake)),
          error: "Please use a valid email provider",
        },
      ];

      for (const validation of validations) {
        if (validation.check) {
          newErrors.email = validation.error;
          break;
        }
      }
    }

    // Phone is required
    const phoneRegex = /^[0-9+\-\s()]+$/;
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    } else if (formData.phone.replace(/[^0-9]/g, "").length < 10) {
      newErrors.phone = "Phone number must be at least 10 digits";
    }

    // Password validation
    const hasMinLength = formData.password && formData.password.length >= 8;
    const hasUppercase = /[A-Z]/.test(formData.password || "");
    const hasNumber = /\d/.test(formData.password || "");
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>-]/.test(
      formData.password || ""
    );

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else {
      if (!hasMinLength)
        newErrors.password = "Password must be at least 8 characters";
      else if (!hasUppercase)
        newErrors.password =
          "Password must contain at least one uppercase letter";
      else if (!hasNumber)
        newErrors.password = "Password must contain at least one number";
      else if (!hasSpecialChar)
        newErrors.password =
          "Password must contain at least one special character";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast("Please fix the errors in the form", "error");
      return;
    }

    setIsLoading(true);
    try {
      console.log("Attempting signup with data:", {
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        // Don't log password for security
      });

      await AuthService.userSignup({
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        password: formData.password,
      });

      showToast(
        "Registration successful! Please verify your identity.",
        "success"
      );
      setShowValidationModal(true);
    } catch (error) {
      let errorMessage = "Registration failed. Please try again.";
      if (error.message.includes("Network error")) {
        errorMessage = "Network error. Please check your internet connection and try again.";
      } else if (error.message.includes("Server error")) {
        errorMessage = "Server is currently unavailable. Please try again in a few minutes.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      showToast(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google Sign-In
  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      console.log("Initiating Google Sign-In...");
      const result = await GoogleAuthService.signIn();

      console.log("Google Sign-In successful:", result.userInfo);

      // Send Google user data to your backend
      const response = await AuthService.googleSignup(result.userInfo);

      console.log("Google signup response:", response);

      // Store tokens if provided
      if (response.tokens) {
        AuthService.storeTokens(response.tokens);
      }

      showToast(
        `Welcome ${result.userInfo.fullName}! Registration successful.`,
        "success"
      );

      // Update form data with Google info for validation modal
      setFormData((prev) => ({
        ...prev,
        fullName: result.userInfo.fullName,
        email: result.userInfo.email,
      }));

      // Show validation modal if verification is required
      if (response.requiresVerification) {
        setShowValidationModal(true);
      } else {
        // Redirect to login page after successful signup (no immediate dashboard access)
        console.log("[SignUp] Redirecting to login");
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      }
    } catch (error) {
      console.error("Google Sign-In error:", error);
      let errorMessage = "Google Sign-In failed. Please try again.";

      if (error.message.includes("popup_closed_by_user")) {
        errorMessage = "Sign-in was cancelled. Please try again.";
      } else if (error.message.includes("Network error")) {
        errorMessage = "Network error. Please check your internet connection.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, "error");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleValidateCode = async (validationData) => {
    setIsValidating(true);
    try {
      console.log("Validating code:", {
        email: validationData.email,
        code: validationData.code,
      });
      const response = await AuthService.validateUserWithCode(validationData);
      console.log("Validation response:", response);

      // Store the REAL tokens from validation response
      if (response.tokens && response.tokens.accessToken) {
        console.log("Storing authentication tokens from validation");
        AuthService.storeTokens(response.tokens);
      } else {
        console.warn(
          "No tokens in validation response. User may not be fully authenticated."
        );
      }

      showToast("Identity verified successfully! Welcome to DHMS.", "success");
      setShowValidationModal(false);

      // Verify tokens are stored before redirect
      const storedToken = AuthService.getAccessToken();
      console.log(
        "[SignUp] Token verification before redirect:",
        !!storedToken
      );

      if (!storedToken) {
        console.error("[SignUp] WARNING: No token found after validation!");
        showToast(
          "Authentication error. Please try logging in again.",
          "error"
        );
        return;
      }

      // Redirect to login after successful verification (user should sign in)
      setTimeout(() => {
        console.log("[SignUp] Redirecting to login after verification");
        navigate("/login");
      }, 1500);
    } catch (error) {
      console.error("Code validation error:", error);
      showToast(
        error.message || "Invalid verification code. Please try again.",
        "error"
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidateId = async (validationData) => {
    setIsValidating(true);
    try {
      await AuthService.validateUserWithId(validationData);
      showToast("Identity verified successfully! Welcome to DHMS.", "success");
      setShowValidationModal(false);
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (error) {
      showToast(error.message || "Invalid unique ID. Please try again.", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleResendCode = async (email) => {
    try {
      await AuthService.requestVerificationCode(email);
      showToast("Verification code has been resent to your email.", "success");
    } catch (error) {
      console.error("Resend code error:", error);
      showToast(
        error.message || "Failed to resend code. Please try again.",
        "error"
      );
    }
  };

  const handleCloseValidationModal = () => {
    setShowValidationModal(false);
    showToast(
      "Verification cancelled. You can complete it later from your profile.",
      "info"
    );
  };

  // Password validation
  const hasMinLength = (formData.password || "").length >= 8;
  const hasUppercase = /[A-Z]/.test(formData.password || "");
  const hasNumber = /\d/.test(formData.password || "");
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>-]/.test(
    formData.password || ""
  );

  return (
    <>
      <div className="S-container">
        <div className="S-logo">
          <a href="/">
            <img
              src={logo || "/placeholder.svg"}
              alt="IT Service Desk Africa Logo"
              className="S-logo-image"
            />
          </a>
        </div>
        <div className="S-left-section">
          <img
            src={signup || "/placeholder.svg"}
            alt="Happy family using devices"
            className="S-family-image"
          />
        </div>
        <div className="S-right-section">
          <div className="S-form-container">
            <div className="S-welcome-section">
              <h1 className="S-welcome-title">
                <span className="S-wave-emoji">👋</span> Welcome!!!
              </h1>
              <p className="S-welcome-subtitle">
                Sign up for Family DHMS and keep your family's devices safe,
                secure, and running smoothly.
              </p>
            </div>
            <form className="S-form" onSubmit={handleSubmit}>
              <div className="S-form-group">
                <label htmlFor="fullName">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={errors.fullName ? "error" : ""}
                  disabled={isLoading || isGoogleLoading}
                />
                {errors.fullName && (
                  <span className="S-error-message">{errors.fullName}</span>
                )}
              </div>
              <div className="S-form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className={errors.email ? "error" : ""}
                  disabled={isLoading || isGoogleLoading}
                />
                {errors.email && (
                  <span className="S-error-message">{errors.email}</span>
                )}
              </div>
              <div className="S-form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                  className={errors.phone ? "error" : ""}
                  disabled={isLoading || isGoogleLoading}
                />
                {errors.phone && (
                  <span className="S-error-message">{errors.phone}</span>
                )}
              </div>
              <div className="S-form-group">
                <label htmlFor="password">Password</label>
                <div className="S-password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => setIsPasswordFocused(false)}
                    placeholder="Password"
                    className={errors.password ? "error" : ""}
                    disabled={isLoading || isGoogleLoading}
                  />
                  <button
                    type="button"
                    className="S-password-toggle"
                    onClick={togglePasswordVisibility}
                    disabled={isLoading || isGoogleLoading}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <span className="S-error-message">{errors.password}</span>
                )}
              </div>
              {isPasswordFocused && (
                <div className="S-password-requirements">
                  <p className="S-requirements-title">
                    Your password must include at least
                  </p>
                  <div className="S-requirements-list">
                    <div className={`S-requirement${hasMinLength ? " S-met" : ""}`}>
                      <CheckCircleIcon met={hasMinLength} />
                      <span>Minimum of 8 characters</span>
                    </div>
                    <div className={`S-requirement${hasUppercase ? " S-met" : ""}`}>
                      <CheckCircleIcon met={hasUppercase} />
                      <span>One UPPERCASE letter</span>
                    </div>
                    <div className={`S-requirement${hasNumber ? " S-met" : ""}`}>
                      <CheckCircleIcon met={hasNumber} />
                      <span>one number</span>
                    </div>
                    <div className={`S-requirement${hasSpecialChar ? " S-met" : ""}`}>
                      <CheckCircleIcon met={hasSpecialChar} />
                      <span>One special character (e.g !"@#%^&?)</span>
                    </div>
                  </div>
                </div>
              )}
              <button
                type="submit"
                className="S-btn"
                disabled={isLoading || isGoogleLoading}
              >
                {isLoading ? (
                  <>
                    <Loader size={16} className="S-spinner" />
                    Creating Account...
                  </>
                ) : (
                  "Sign up"
                )}
              </button>
              <div className="S-divider">or</div>
              <button
                type="button"
                className="S-google-btn"
                onClick={handleGoogleSignIn}
                disabled={isLoading || isGoogleLoading}
              >
                {isGoogleLoading ? (
                  <>
                    <Loader size={16} className="S-spinner" />
                    Signing in with Google...
                  </>
                ) : (
                  <>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      className="S-google-icon"
                    >
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign up with Google
                  </>
                )}
              </button>
            </form>
            <p className="S-login-link">
              Already have an account? <Link to="/login">Login</Link>
            </p>
          </div>
        </div>
      </div>
      {/* Validation Modal */}
      <ValidationModal
        isOpen={showValidationModal}
        onClose={handleCloseValidationModal}
        onValidateCode={handleValidateCode}
        onValidateId={handleValidateId}
        onResendCode={handleResendCode}
        userEmail={formData.email}
        isLoading={isValidating}
      />
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
        duration={5000}
      />
    </>
  );
};

export default SignupForm;
